import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/LangProvider';

type Opt = { id: string; labelEn: string; labelFr: string };
type Meal = { id: string; nameEn: string; nameFr: string; options: Opt[] };
type Config = { departments: Opt[]; dietary: Opt[]; accessibility: Opt[]; meals: Meal[]; teamsLink?: string };
type Submission = { email: string; data: Record<string, unknown>; submittedAt: string };

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `Error ${res.status}`;
    try { const j = await res.json(); msg = j.error || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  return res.json();
}

type Key = 'departments' | 'dietary' | 'accessibility';

export default function AdminRegister() {
  const { t } = useT();
  const [tab, setTab] = useState<'form' | 'allowlist' | 'submissions'>('form');
  const [cfg, setCfg] = useState<Config>({ departments: [], dietary: [], accessibility: [], meals: [], teamsLink: '' });
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [subs, setSubs] = useState<Submission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [allowSearch, setAllowSearch] = useState('');
  const [subSearch, setSubSearch] = useState('');

  const load = async () => {
    try {
      const [c, a, s] = await Promise.all([
        handle<Config>(await fetch('/api/reg-config')),
        handle<{ emails: string[] }>(await fetch('/api/reg-allowlist')),
        handle<{ items: Submission[] }>(await fetch('/api/reg-submissions'))
      ]);
      setCfg(c); setEmails(a.emails); setSubs(s.items);
    } catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async (next: Config) => {
    setBusy(true); setError(null);
    try { await handle(await fetch('/api/reg-config-save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(next) })); setCfg(next); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addOpt = (key: Key) => {
    const en = prompt('Option label (English):'); if (!en) return;
    const fr = prompt('Libellé (Français):'); if (!fr) return;
    const opt: Opt = { id: 'o-' + Date.now(), labelEn: en.trim(), labelFr: fr.trim() };
    saveCfg({ ...cfg, [key]: [...cfg[key], opt] });
  };
  const removeOpt = (key: Key, id: string) => saveCfg({ ...cfg, [key]: cfg[key].filter((o) => o.id !== id) });

  const onCsv = async (file: File) => {
    setBusy(true); setError(null); setMsg(null);
    try {
      const text = await file.text();
      // Extract emails from CSV/text: split on commas, newlines, semicolons, whitespace
      const found = text.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter((x) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
      const unique = [...new Set(found)];
      if (unique.length === 0) { setError('No valid email addresses found in the file.'); setBusy(false); return; }
      const r = await handle<{ emails: string[] }>(await fetch('/api/reg-allowlist-save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emails: unique }) }));
      setEmails(r.emails);
      setMsg(`Allowlist updated — ${r.emails.length} email${r.emails.length === 1 ? '' : 's'} loaded.`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addOneEmail = async () => {
    const em = newEmail.trim().toLowerCase();
    if (!em.includes('@')) { setError('Invalid email address.'); return; }
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await handle<{ emails: string[] }>(await fetch('/api/reg-allowlist-add', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: em }) }));
      setEmails(r.emails); setNewEmail(''); setMsg('Email added.');
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const removeEmail = async (em: string) => {
    if (!confirm(`Remove ${em} from the allowlist? They will no longer be able to register.`)) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const next = emails.filter((e) => e !== em);
      const r = await handle<{ emails: string[] }>(await fetch('/api/reg-allowlist-save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emails: next }) }));
      setEmails(r.emails); setMsg(`${em} removed.`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const exportSubs = () => {
    const optLabel = (key: Key, id: string) => cfg[key].find((o) => o.id === id)?.labelEn || id;
    const mealHeaders = cfg.meals.map((m) => m.nameEn);
    const headers = ['Email', 'Full Name', 'Department', 'Manager', 'Dietary', 'Dietary Other', 'Accessibility', 'Accessibility Other', ...mealHeaders, 'Has Badge', 'Submitted'];
    const rows = subs.map((s) => {
      const d = s.data as Record<string, unknown>;
      const diet = (d.dietary as string[] || []).map((id) => optLabel('dietary', id)).join('; ');
      const acc = (d.accessibility as string[] || []).map((id) => optLabel('accessibility', id)).join('; ');
      const mealCells = cfg.meals.map((m) => {
        const choiceId = (d.meals as Record<string, string> || {})[m.id];
        return m.options.find((o) => o.id === choiceId)?.labelEn || choiceId || '';
      });
      const cells = [
        s.email, d.fullName as string || '', optLabel('departments', d.department as string || ''),
        d.manager as string || '', diet, d.dietaryOther as string || '', acc, d.accessibilityOther as string || '',
        ...mealCells, d.badge as string || '', s.submittedAt || ''
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const clearSub = async (email: string) => {
    if (!confirm(`Clear ${email}'s registration? They'll be able to register again.`)) return;
    setBusy(true);
    try { await handle(await fetch('/api/reg-clear', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) })); await load(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };



  const OptList = ({ title, k }: { title: string; k: Key }) => (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <p className="font-display text-xl text-ink">{title}</p>
        <button onClick={() => addOpt(k)} disabled={busy} className="text-sm text-brand-red hover:underline">{t('admin.reg.addOption')}</button>
      </div>
      <div className="space-y-2">
        {cfg[k].map((o) => (
          <div key={o.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-bg-base border border-white/[0.06]">
            <span className="text-ink text-sm">{o.labelEn} <span className="text-ink-faint">/ {o.labelFr}</span></span>
            <button onClick={() => removeOpt(k, o.id)} className="text-ink-faint hover:text-brand-red text-sm">{t('admin.reg.remove')}</button>
          </div>
        ))}
        {cfg[k].length === 0 && <p className="text-ink-faint text-sm">{t('admin.reg.noOptions')}</p>}
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 pt-36 pb-32">
      <Link to="/admin" className="text-xs text-ink-muted hover:text-ink lowercase">{t('admin.back')}</Link>
      <p className="text-xs tracking-[0.25em] uppercase text-brand-red mt-5 mb-3 font-mono">{t('admin.reg.kicker')}</p>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight text-ink">{t('admin.reg.title')}</h1>

      <div className="flex gap-2 mt-8 mb-8 border-b border-white/[0.07] pb-4 flex-wrap">
        {([['form', t('admin.reg.tabForm')], ['allowlist', t('admin.reg.tabAllowlist')], ['submissions', t('admin.reg.tabSubmissions')]] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === id ? 'bg-brand-red text-white' : 'bg-bg-elev text-ink-muted hover:text-ink border border-white/[0.08]'}`}>{label}</button>
        ))}
      </div>

      {error && <div className="mb-6 p-4 rounded-xl bg-red-950/50 border border-red-700/50 text-red-300 text-sm">{error}</div>}
      {msg && <div className="mb-6 p-4 rounded-xl bg-emerald-950/50 border border-emerald-700/50 text-emerald-300 text-sm">{msg}</div>}

      {tab === 'form' && (
        <div className="space-y-4">
          <p className="text-ink-muted text-sm">{t('admin.reg.formLead')}</p>
          <OptList title={t('admin.reg.departments')} k="departments" />
          <OptList title={t('admin.reg.dietary')} k="dietary" />
          <OptList title={t('admin.reg.accessibility')} k="accessibility" />
        </div>
      )}

      {tab === 'allowlist' && (
        <div className="space-y-6">
          <div className="card p-6">
            <p className="font-display text-xl text-ink mb-2">{t('admin.reg.uploadTitle')}</p>
            <p className="text-ink-muted text-sm mb-5">{t('admin.reg.uploadLead')}</p>
            <label className="inline-flex items-center gap-2 bg-brand-red text-white text-sm rounded-full px-5 py-2.5 cursor-pointer hover:bg-brand-red-soft transition-colors">
              {t('admin.reg.chooseCsv')}
              <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onCsv(f); e.currentTarget.value = ''; }} />
            </label>
          </div>
          <div className="card p-6">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <p className="text-xs uppercase tracking-wider text-ink-muted font-mono">{emails.length} {t('admin.reg.allowedEmails')}</p>
              <input value={allowSearch} onChange={(e) => setAllowSearch(e.target.value)} placeholder="Search emails…" className="bg-bg-base border border-white/[0.12] rounded-lg px-3 py-1.5 text-sm text-ink outline-none focus:border-brand-red w-56" />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mb-5">
              <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="colleague@tecsys.com"
                className="flex-1 bg-bg-base border border-white/[0.12] rounded-lg px-3 py-2 text-sm text-ink outline-none focus:border-brand-red" />
              <button onClick={addOneEmail} disabled={busy || !newEmail.includes('@')} className="bg-brand-red text-white text-sm rounded-lg px-5 py-2 disabled:opacity-40 hover:bg-brand-red-soft whitespace-nowrap">Add email</button>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1">
              {emails.filter((em) => em.toLowerCase().includes(allowSearch.toLowerCase())).map((em) => <div key={em} className="flex items-center justify-between text-sm text-ink py-2 border-b border-white/[0.04] last:border-0"><span>{em}</span><button onClick={() => removeEmail(em)} disabled={busy} className="text-xs text-ink-faint hover:text-brand-red disabled:opacity-40 ml-3">Remove</button></div>)}
              {emails.length === 0 && <p className="text-ink-faint text-sm">{t('admin.reg.noEmails')}</p>}
            </div>
          </div>
        </div>
      )}

      {tab === 'submissions' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-ink-muted text-sm">{subs.length} {t('admin.reg.registrations')}</p>
              <input value={subSearch} onChange={(e) => setSubSearch(e.target.value)} placeholder="Search name or email…" className="bg-bg-base border border-white/[0.12] rounded-lg px-3 py-1.5 text-sm text-ink outline-none focus:border-brand-red w-64" />
            </div>
            <button onClick={exportSubs} className="bg-brand-red text-white text-sm rounded-full px-5 py-2 hover:bg-brand-red-soft">{t('admin.reg.exportCsv')}</button>
          </div>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-muted border-b border-white/[0.06] whitespace-nowrap">
                  {[t('admin.reg.colName'), t('admin.reg.colEmail'), t('admin.reg.colDept'), t('admin.reg.colManager'), t('admin.reg.dietary'), 'Dietary (other)', t('admin.reg.accessibility'), 'Accessibility (other)', ...cfg.meals.map((m) => m.nameEn), t('admin.reg.colBadge'), t('admin.reg.colSubmitted'), ''].map((h, i) => <th key={i} className="px-4 py-3 font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {subs.filter((sub) => { const q = subSearch.toLowerCase(); const dd = sub.data as Record<string, unknown>; return !q || sub.email.toLowerCase().includes(q) || String(dd.fullName || '').toLowerCase().includes(q); }).map((s) => {
                  const d = s.data as Record<string, unknown>;
                  const dept = cfg.departments.find((o) => o.id === d.department)?.labelEn || (d.department as string) || '—';
                  const diet = (d.dietary as string[] || []).map((id) => cfg.dietary.find((o) => o.id === id)?.labelEn || id).join(', ') || '—';
                  const acc = (d.accessibility as string[] || []).map((id) => cfg.accessibility.find((o) => o.id === id)?.labelEn || id).join(', ') || '—';
                  const mealChoices = (d.meals as Record<string, string>) || {};
                  return (
                    <tr key={s.email} className="border-b border-white/[0.04] last:border-0 whitespace-nowrap">
                      <td className="px-4 py-3 text-white">{d.fullName as string || '—'}</td>
                      <td className="px-4 py-3 text-ink-muted">{s.email}</td>
                      <td className="px-4 py-3 text-ink">{dept}</td>
                      <td className="px-4 py-3 text-ink">{d.manager as string || '—'}</td>
                      <td className="px-4 py-3 text-ink">{diet}</td>
                      <td className="px-4 py-3 text-ink-muted">{(d.dietaryOther as string) || '—'}</td>
                      <td className="px-4 py-3 text-ink">{acc}</td>
                      <td className="px-4 py-3 text-ink-muted">{(d.accessibilityOther as string) || '—'}</td>
                      {cfg.meals.map((m) => (
                        <td key={m.id} className="px-4 py-3 text-ink">{m.options.find((o) => o.id === mealChoices[m.id])?.labelEn || mealChoices[m.id] || '—'}</td>
                      ))}
                      <td className="px-4 py-3 text-ink">{d.badge as string || '—'}</td>
                      <td className="px-4 py-3 text-ink-faint">{s.submittedAt ? new Date(s.submittedAt).toLocaleDateString() : ''}</td>
                      <td className="px-4 py-3"><button onClick={() => clearSub(s.email)} className="text-xs text-ink-faint hover:text-brand-red">{t('admin.reg.clear')}</button></td>
                    </tr>
                  );
                })}
                {subs.length === 0 && <tr><td colSpan={12 + cfg.meals.length} className="px-4 py-6 text-ink-muted text-center">{t('admin.reg.noRegs')}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
